import { defineComponent as _defineComponent } from 'vue'
import Vue, { computed, reactive, ref, watch } from 'vue';
import InfoBlock, { InformationBlock } from 'ah-common-lib/src/common/components/InfoBlock.vue';
import { CompoundValidation, FormDefinition, FormValidation } from 'ah-common-lib/src/form/interfaces';
import { submitForm, toDataModel, updateModel } from 'ah-common-lib/src/form/helpers';
import {
  BaseIndividual,
  checkAddressHistoryValidity,
  Client,
  ClientFileCategories,
  FileCategory,
  UnsubmittedUbo,
  UnsubmittedUboFile,
  UploadedFile,
} from 'ah-api-gateways';
import { makeMemberForm } from './reviewForms';
import DocumentsUploader from '@/app/components/settings/client/DocumentsUploader.vue';
import { cloneDeep } from 'lodash';
import UboDocumentsEditor from '../../ubos/UboDocumentsEditor.vue';
import AccordionBox from '@/app/components/common/AccordionBox.vue';
import { useRequestManager } from 'ah-common-lib/src/requestManager/useRequestManager';
import { useAuthStore } from '@/app/store/authStore';
import { getServices } from '@/app/services';
import { forkJoin, from, of } from 'rxjs';
import { defaultIfEmpty, mergeMap } from 'rxjs/operators';
import { useIndividualSettingsStore } from '@/app/store/individualSettingsModule';
import { countryNameFromCC } from 'ah-common-lib/src/helpers/countries';
import { formatDate } from 'ah-common-lib/src/helpers/time';
import RegistrationAddressForm from '../../registration/forms/common/RegistrationAddressForm.vue';
import ReviewScreenPreviousAddressesForms from './ReviewScreenPreviousAddressesForms.vue';
import { useFEFeatureFlag } from 'ah-common-stores';
import { useToast } from 'ah-common-lib/src/toast';

type UboForm = FormDefinition & {
  id: string;
  ubo: UnsubmittedUbo;
  currentAddressValidation: FormValidation | null;
  previousAddressesValidation: CompoundValidation | null;
};


export default /*#__PURE__*/_defineComponent({
  __name: 'DocumentsReview',
  props: {
    client: null,
    documents: { default: () => [] },
    ubos: { default: () => [] },
    editable: { type: Boolean, default: true }
  },
  emits: ["update:ubos"],
  setup(__props: any, { emit }: { emit: ({ (e: 'update:ubos', ubos: UnsubmittedUbo[]): void }), expose: any, slots: any, attrs: any }) {

const props = __props as { client: Client, documents: UploadedFile[], ubos: UnsubmittedUbo[], editable: boolean };

const requestManager = useRequestManager().manager;

const services = getServices();

const toast = useToast();

const authStore = useAuthStore();

const individualStore = useIndividualSettingsStore();





const state = reactive<{
  editing: boolean;
  uboForms: UboForm[];
  showErrorMessage: boolean;
}>({
  editing: false,
  uboForms: [] as UboForm[],
  showErrorMessage: false,
});

const hideOnboardingDocumentsUploader = useFEFeatureFlag('hideOnboardingDocumentsUploader');

function isUboAUKResident(ubo?: UnsubmittedUbo): boolean {
  return ubo?.currentAddress?.countryCode === 'GB';
}

function includesFile(category: FileCategory) {
  return props.documents.find((doc) => doc.category === category) ? 'Uploaded' : 'Upload Later';
}

const signatoryDocsBlock = computed<InformationBlock[]>(() => [
  { label: 'Proof of ID', key: 'poi', value: includesFile(ClientFileCategories.PHOTO_ID), cols: 4 },
  { label: 'Proof of Address', key: 'poa', value: includesFile(ClientFileCategories.PROOF_OF_ADDRESS), cols: 4 },
]);

const companyDocsBlock = computed<InformationBlock[]>(() => [
  {
    label: 'Certificate of incorporation',
    key: 'incCertificate',
    value: includesFile(ClientFileCategories.INCORPORATED_DOCUMENT),
    cols: 4,
  },
  {
    label: 'Audited financial statements/latest accounts',
    key: 'financialStatements',
    value: includesFile(ClientFileCategories.FINANCIAL_STATEMENT),
    cols: 4,
  },
  {
    label: 'Sample Invoice/Proof of Business Activity',
    key: 'pba',
    value: includesFile(ClientFileCategories.SAMPLE_INVOICE),
    cols: 4,
  },
]);

const uboInfoBlock = ref<InformationBlock[]>([
  { label: 'Are there any UBOs?', key: 'any', value: props.ubos.length > 0 ? 'Yes' : 'No', cols: 4 },
]);

const uboBlock = [
  { label: 'Title', key: 'title', cols: 4 },
  { label: 'First Name', key: 'firstName', cols: 4 },
  { label: 'Last Name', key: 'lastName', cols: 4 },
  { label: 'Email Address', key: 'email', cols: 4 },
  { label: 'Mobile Number', key: 'phoneNumber', cols: 4 },
  { label: 'Date of Birth', key: 'birthDate', cols: 4 },
  { label: 'Percentage of Ownership', key: 'ownershipPercentage', cols: 4 },
  { label: 'Proof of ID', key: 'poi', value: '', cols: 4 },
  { label: 'Proof of Address', key: 'poa', value: '', cols: 4 },
];

const filteredUboBlock = computed(() =>
  uboBlock.filter((item) => !hideOnboardingDocumentsUploader.value || (item.key !== 'poi' && item.key !== 'poa'))
);

const uboAddressHistoryBlock = computed(() => {
  return (ubo: UnsubmittedUbo) => [
    { label: 'Address Line 1', key: 'addressLine', cols: 4 },
    { label: 'City', key: 'city', cols: 4 },
    { label: 'State/Province/County', key: 'stateOrProvince', cols: 4 },
    { label: 'Postcode/Zip Code', key: 'postalCode', cols: 4 },
    { label: 'Country', key: 'countryCode', cols: 4 },
    ...(isUboAUKResident(ubo)
      ? [
          {
            label: 'When did the UBO move to this address?',
            key: 'residingFrom',
            cols: 8,
          },
        ]
      : []),
  ];
});

const isEditable = computed(() => props.editable !== false);

const areUboFormsInvalid = computed(() =>
  state.uboForms.some((uboForm) => {
    return !!(
      uboForm.validation?.$invalid ||
      uboForm.currentAddressValidation?.$invalid ||
      uboForm.previousAddressesValidation?.$invalid
    );
  })
);

function checkUboAddressHistoryValidity(ubo: UnsubmittedUbo) {
  const addressHistoryValidity = checkAddressHistoryValidity({
    currentAddress: { ...ubo.currentAddress },
    previousAddresses: ubo.previousAddresses || [],
  });
  return !isUboAUKResident(ubo) || (addressHistoryValidity.age && addressHistoryValidity.gaps);
}

function uboSaveRequests() {
  const clientId = authStore.loggedInIdentity!.client!.id;
  return state.uboForms.map((uboForm, index) => {
    return services.compliance
      .updateUboUser(clientId, uboForm.ubo.id!, toDataModel(state.uboForms[index].form) as BaseIndividual)
      .pipe(
        mergeMap(() =>
          forkJoin(
            (uboForm.ubo.readyToUpload || []).map((file) => {
              if (file.file) {
                return services.compliance.submitUboDocument(clientId, uboForm.ubo.id!, file.category, file.file!);
              } else {
                const toBeRemoved = uboForm.ubo.documents.find((doc) => doc.category === file.category);
                if (toBeRemoved) {
                  return services.compliance.removeUboDocument(clientId, uboForm.ubo.id!, toBeRemoved.id);
                }
                return of(null);
              }
            })
          ).pipe(defaultIfEmpty([] as any))
        )
      );
  });
}

function save() {
  state.uboForms.forEach((uboForm) => {
    if (uboForm.validation) {
      submitForm(uboForm.validation);
    }
    if (uboForm.currentAddressValidation) {
      submitForm(uboForm.currentAddressValidation);
    }
    if (uboForm.previousAddressesValidation) {
      uboForm.previousAddressesValidation.validations.forEach(submitForm);
    }
  });

  if (areUboFormsInvalid.value) {
    toast?.error('Form contains errors! Please review and resubmit.');
    return;
  }

  if (state.uboForms.some((uboForm) => !checkUboAddressHistoryValidity(uboForm.ubo))) {
    state.showErrorMessage = true;
    toast?.error(
      'Please review the address history. Ensure it spans at least 3 years with no gaps for UK resident UBOs.'
    );
    return;
  }

  requestManager
    .currentOrNew('saveUbos', forkJoin([...uboSaveRequests(), from(individualStore.loadClientDocuments(true))]))
    .subscribe(() => {
      toast?.success('Changes applied successfully');
      emit(
        'update:ubos',
        state.uboForms.map((uboForm) => ({
          ...uboForm.ubo,
          ...toDataModel(uboForm.form),
        }))
      );
      setTimeout(() => {
        state.editing = false;
        state.showErrorMessage = false;
      });
    });
}

function uboContainsFile(ubo: UnsubmittedUbo, category: FileCategory) {
  return !!ubo.documents?.find((f) => f.category === category);
}

function resetUbos(loadFiles: boolean = false) {
  const ubos = cloneDeep(props.ubos) as UnsubmittedUbo[];
  const uboForms: UboForm[] = [];
  ubos.forEach((ubo) => {
    const uboForm = state.uboForms.find((f) => f.id === ubo.id);
    uboForms.push({
      ...uboForm,
      id: ubo.id!,
      form: makeMemberForm(true),
      validation: null,
      currentAddressValidation: null,
      previousAddressesValidation: null,
      ubo,
    });
    updateModel(uboForms[uboForms.length - 1].form, ubo);
    if (loadFiles) {
      requestManager
        .sameOrCancelAndNew(
          'getUboDocuments',
          services.compliance.getUboDocuments(authStore.loggedInIdentity!.client!.id, ubo.id!, {
            errors: { silent: true },
          })
        )
        .subscribe((response) => {
          Vue.set(ubo, 'documents', response);
        });
    }
  });
  state.uboForms = uboForms as any;
}

function onFileSelected(ubo: UnsubmittedUbo, fileData: UnsubmittedUboFile) {
  let readyToUpload = ubo.readyToUpload;
  if (!readyToUpload) {
    Vue.set(ubo, 'readyToUpload', []);
    readyToUpload = ubo.readyToUpload!;
  }
  let uploadFile = readyToUpload.find((i) => i.category === fileData.category);

  if (!uploadFile) {
    uploadFile = { ...fileData };
    readyToUpload.push(uploadFile);
  } else {
    uploadFile.file = fileData.file;
  }
}

watch(
  () => props.ubos,
  () => resetUbos(true),
  { immediate: true }
);

watch(
  () => state.editing,
  () => {
    if (state.editing) {
      resetUbos();
    }
  }
);

return { __sfc: true,requestManager, services, toast, authStore, individualStore, emit, props, state, hideOnboardingDocumentsUploader, isUboAUKResident, includesFile, signatoryDocsBlock, companyDocsBlock, uboInfoBlock, uboBlock, filteredUboBlock, uboAddressHistoryBlock, isEditable, areUboFormsInvalid, checkUboAddressHistoryValidity, uboSaveRequests, save, uboContainsFile, resetUbos, onFileSelected, InfoBlock, ClientFileCategories, DocumentsUploader, UboDocumentsEditor, AccordionBox, countryNameFromCC, formatDate, RegistrationAddressForm, ReviewScreenPreviousAddressesForms }
}

})